// @ts-nocheck
/**
 * PersonalizadorPreviewCanvas
 * Renderiza bandera y nombre en un canvas superpuesto sobre la imagen target de la PDP.
 */
class PersonalizadorPreviewCanvas {
  constructor() {
    this.states = [];
    this.resizeScheduled = false;
    this.flagImageCache = new Map();
    this.asaImageCache = new Map();
    this.colorImageCache = new Map();

    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleSlideshowSelect = this.handleSlideshowSelect.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handlePreviewFabClick = this.handlePreviewFabClick.bind(this);

    this.bindAllBlocks();
    this.watchStickyAddToCart();

    document.addEventListener('variant:update', this.handleVariantUpdate);
    document.addEventListener('slideshow:select', this.handleSlideshowSelect, true);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
    document.addEventListener('click', this.handlePreviewFabClick);
  }

  bindAllBlocks() {
    const wrappers = document.querySelectorAll('.personalizador-wrapper');
    wrappers.forEach((wrapper) => this.bindBlock(wrapper));
  }

  bindBlock(block) {
    if (!(block instanceof HTMLElement)) return;

    const wrapper = block.classList.contains('personalizador-wrapper')
      ? block
      : block.closest('.personalizador-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    if (wrapper.dataset.previewBound === 'true') return;

    const nameBlocks = Array.from(
      wrapper.querySelectorAll('.personalizador__block--nombre[data-preview-target-index]')
    );
    const asaBlock = wrapper.querySelector('.personalizador__block--asas');

    if (nameBlocks.length === 0 && !(asaBlock instanceof HTMLElement)) return;

    wrapper.dataset.previewBound = 'true';

    if (nameBlocks.length === 0) {
      this._createState(wrapper, null, asaBlock, true);
    } else {
      nameBlocks.forEach((nameBlock, i) => {
        this._createState(wrapper, nameBlock, i === 0 ? asaBlock : null, i === 0);
      });
    }
  }

  _createState(wrapper, nameBlock, asaBlock, isPrimary) {
    const nameInput = nameBlock instanceof HTMLElement
      ? nameBlock.querySelector('[data-personalizador-visible-property]')
      : null;
    const safeNameInput = nameInput instanceof HTMLInputElement || nameInput instanceof HTMLTextAreaElement
      ? nameInput
      : null;

    this._stateIdCounter = (this._stateIdCounter || 0) + 1;

    const state = {
      wrapper,
      nameBlock: nameBlock instanceof HTMLElement ? nameBlock : null,
      asaBlock: asaBlock instanceof HTMLElement ? asaBlock : null,
      nameInput: safeNameInput,
      isPrimary: !!isPrimary,
      stateId: String(this._stateIdCounter),
      gallery: null,
      layer: null,
      canvas: null,
      targetContainer: null,
      targetImage: null,
      hasAutoScrolled: false,
      debounceTimer: null,
      swapInfo: null,
      onImageLoad: () => this.render(state),
      onInput: (e) => {
        if (state.nameInput && e.target === state.nameInput) {
          this.enforceTextLimit(state);
        }
        this.scheduleDebouncedRender(state);
      },
      onImmediate: () => this.renderNow(state),
      onActivateTarget: (event) => this.handleInteractiveTargetSync(state, event),
      onModeChange: (event) => this.handleModeChange(state, event),
    };

    wrapper.addEventListener('input', state.onInput);
    wrapper.addEventListener('change', state.onImmediate);
    wrapper.addEventListener('personalizador:preview-sync', state.onInput);
    wrapper.addEventListener('focusin', state.onActivateTarget);
    wrapper.addEventListener('pointerdown', state.onActivateTarget);
    wrapper.addEventListener('personalizador:mode-change', state.onModeChange);

    if (state.nameInput) {
      state.nameInput.addEventListener('blur', state.onImmediate);
    }

    this.states.push(state);
    this.refreshTarget(state, { allowAutoScroll: true });
    this.render(state);
  }

  unbindState(state) {
    window.clearTimeout(state.debounceTimer);

    state.wrapper.removeEventListener('input', state.onInput);
    state.wrapper.removeEventListener('change', state.onImmediate);
    state.wrapper.removeEventListener('personalizador:preview-sync', state.onInput);
    state.wrapper.removeEventListener('focusin', state.onActivateTarget);
    state.wrapper.removeEventListener('pointerdown', state.onActivateTarget);
    state.wrapper.removeEventListener('personalizador:mode-change', state.onModeChange);

    if (state.nameInput) {
      state.nameInput.removeEventListener('blur', state.onImmediate);
    }

    if (state.targetImage) {
      state.targetImage.removeEventListener('load', state.onImageLoad);
    }

    this.restoreGridOrder(state);

    if (state.layer && state.layer.parentElement) {
      state.layer.parentElement.removeChild(state.layer);
    }
  }

  pruneDisconnectedStates() {
    this.states = this.states.filter((state) => {
      if (state.wrapper.isConnected) return true;
      this.unbindState(state);
      return false;
    });
  }

  handleVariantUpdate() {
    window.requestAnimationFrame(() => {
      this.pruneDisconnectedStates();
      this.bindAllBlocks();

      this.states.forEach((state) => {
        this.refreshTarget(state, { allowAutoScroll: false });
        this.render(state);
      });
    });
  }

  handleSlideshowSelect(event) {
    const slide = event?.detail?.slide;
    if (!(slide instanceof Element)) return;

    this.states.forEach((state) => {
      if (!state.wrapper.isConnected || !state.gallery) return;
      if (!state.gallery.contains(slide)) return;

      this.refreshTarget(state, { allowAutoScroll: false });
      this.render(state);
    });
  }

  handleResize() {
    if (this.resizeScheduled) return;
    this.resizeScheduled = true;

    window.requestAnimationFrame(() => {
      this.resizeScheduled = false;

      this.pruneDisconnectedStates();
      this.states.forEach((state) => {
        this.refreshTarget(state, { allowAutoScroll: false });
        this.render(state);
      });
    });
  }

  renderAll() {
    this.pruneDisconnectedStates();
    this.states.forEach((state) => {
      this.refreshTarget(state, { allowAutoScroll: false });
      this.render(state);
    });
  }

  scheduleDebouncedRender(state) {
    window.clearTimeout(state.debounceTimer);
    state.debounceTimer = window.setTimeout(() => {
      this.refreshTarget(state, { allowAutoScroll: false });
      this.render(state);
    }, 180);
  }

  renderNow(state) {
    window.clearTimeout(state.debounceTimer);
    this.updateSwapState(state);
    this.refreshTarget(state, { allowAutoScroll: false });
    this.render(state);
  }

  handleInteractiveTargetSync(state, event) {
    const target = event?.target;
    if (!(target instanceof Element)) return;

    // Scope to own blocks — avoid cross-state scrolling with multiple name positions
    const inOwnNameBlock = state.nameBlock instanceof HTMLElement && state.nameBlock.contains(target);
    const inOwnAsaBlock = state.asaBlock instanceof HTMLElement && state.asaBlock.contains(target);
    if (!inOwnNameBlock && !inOwnAsaBlock) return;

    const actionable = target.closest(
      '.personalizador__block--nombre input, .personalizador__block--nombre textarea, .personalizador__block--bandera select, .personalizador__block--asas [data-asa-trigger], .personalizador__block--asas [data-asa-option], .personalizador__block--asas [data-asa-clear]'
    );
    if (!(actionable instanceof Element)) return;

    this.refreshTarget(state, { allowAutoScroll: false });
    if (inOwnNameBlock) {
      this.focusTargetImage(state);
    } else if (inOwnAsaBlock) {
      const gridVisible = this.isElementVisible(state.gallery?.querySelector('.media-gallery__grid'));
      if (!gridVisible) {
        this.focusTargetImage(state, { onlySlideChange: true });
      }
    }
    this.render(state);
  }

  focusTargetImage(state, { onlySlideChange = false, force = false } = {}) {
    if (!(state.targetContainer instanceof HTMLElement)) return;

    const gridVisible = this.isElementVisible(state.gallery?.querySelector('.media-gallery__grid'));
    const slideshow = this.resolveSlideshow(state.gallery);
    const targetIndex = this.getTargetIndex(state);
    let didSwitchSlide = false;

    if (!gridVisible && slideshow && typeof slideshow.select === 'function' && !this.isTargetSlideActive(state.targetContainer)) {
      try {
        slideshow.select(targetIndex, undefined, { animate: false });
        didSwitchSlide = true;
      } catch (_error) {
        // Continue with scroll fallback.
      }
    }

    const shouldScroll = force || didSwitchSlide || (!onlySlideChange && !this.isElementInViewport(state.targetContainer));
    if (shouldScroll) {
      window.setTimeout(() => {
        if (!(state.targetContainer instanceof HTMLElement)) return;
        state.targetContainer.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }, 40);
    }
  }

  watchStickyAddToCart() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!(bar instanceof HTMLElement)) return;

    const syncStuckState = () => {
      document.body.classList.toggle('has-sticky-add-to-cart', bar.dataset.stuck === 'true');
    };

    syncStuckState();

    const observer = new MutationObserver(syncStuckState);
    observer.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  handlePreviewFabClick(event) {
    const target = event?.target;
    if (!(target instanceof Element)) return;

    const fab = target.closest('[data-personalizador-preview-fab]');
    if (!fab) return;

    const wrapper = fab.closest('.personalizador-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const state = this.states.find((s) => s.wrapper === wrapper && s.isPrimary);
    if (!state) return;

    this.refreshTarget(state, { allowAutoScroll: false });
    this.focusTargetImage(state, { force: true });
  }

  refreshTarget(state, { allowAutoScroll }) {
    const previewAnchor = state.nameBlock instanceof HTMLElement ? state.nameBlock : state.wrapper;
    const gallery = this.resolveGallery(previewAnchor);
    if (!(gallery instanceof HTMLElement)) {
      this.detachLayer(state);
      state.gallery = null;
      return;
    }

    state.gallery = gallery;

    const configuredIndex = this.getTargetIndex(state);
    const targetIndex = state.swapInfo ? 0 : configuredIndex;
    const slideshowContainers = Array.from(
      gallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide.product-media-container')
    );
    const gridContainers = Array.from(gallery.querySelectorAll('.media-gallery__grid > .product-media-container'));

    const slideshowTarget = this.pickTargetContainer(slideshowContainers, configuredIndex);
    const gridTarget = this.pickTargetContainer(gridContainers, targetIndex);
    const gridVisible = this.isElementVisible(gallery.querySelector('.media-gallery__grid'));

    const nextTarget = gridVisible ? gridTarget || slideshowTarget : slideshowTarget || gridTarget;
    if (!(nextTarget instanceof HTMLElement)) {
      this.detachLayer(state);
      return;
    }

    this.attachLayer(state, nextTarget);

    const nextImage = nextTarget.querySelector('img.product-media__image');
    if (state.targetImage !== nextImage) {
      if (state.targetImage) {
        state.targetImage.removeEventListener('load', state.onImageLoad);
      }

      state.targetImage = nextImage instanceof HTMLImageElement ? nextImage : null;
      if (state.targetImage) {
        state.targetImage.addEventListener('load', state.onImageLoad);
      }
    }

    if (allowAutoScroll) {
      this.autoScrollToTarget(state, configuredIndex);
    }
  }

  resolveGallery(block) {
    const section = block.closest('.shopify-section');
    const scopedGallery = section ? section.querySelector('media-gallery') : null;
    if (scopedGallery instanceof HTMLElement) return scopedGallery;

    const fallbackGallery = document.querySelector('media-gallery');
    return fallbackGallery instanceof HTMLElement ? fallbackGallery : null;
  }

  resolveSlideshow(gallery) {
    if (!(gallery instanceof HTMLElement)) return null;
    if (gallery.slideshow) return gallery.slideshow;

    const slideshow = gallery.querySelector('slideshow-component[ref="slideshow"]');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  pickTargetContainer(containers, index) {
    if (!Array.isArray(containers) || containers.length === 0) return null;

    const indexed = index >= 0 && index < containers.length ? containers[index] : null;
    if (this.isImageContainer(indexed)) return indexed;

    const firstImage = containers.find((container) => this.isImageContainer(container));
    if (firstImage) return firstImage;

    if (indexed) return indexed;
    return containers[0];
  }

  isImageContainer(container) {
    return container instanceof HTMLElement && container.classList.contains('product-media-container--image');
  }

  isElementVisible(element) {
    if (!(element instanceof Element)) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return element.getClientRects().length > 0;
  }

  isElementInViewport(element) {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw;
  }

  isTargetSlideActive(targetContainer) {
    if (!(targetContainer instanceof HTMLElement)) return true;
    if (targetContainer.tagName.toLowerCase() !== 'slideshow-slide') return true;
    if (targetContainer.hasAttribute('hidden')) return false;
    return targetContainer.getAttribute('aria-hidden') !== 'true';
  }

  attachLayer(state, targetContainer) {
    if (state.targetContainer !== targetContainer) {
      this.detachLayer(state);
      state.targetContainer = targetContainer;
    }

    if (!state.targetContainer) return;

    let layer = Array.from(state.targetContainer.children).find(
      (child) => child instanceof HTMLElement
        && child.classList.contains('personalizador-preview-layer')
        && child.dataset.stateId === state.stateId
    );

    if (!(layer instanceof HTMLElement)) {
      layer = document.createElement('div');
      layer.className = 'personalizador-preview-layer';
      layer.dataset.stateId = state.stateId;
      state.targetContainer.appendChild(layer);
    }

    let canvas = layer.querySelector('canvas.personalizador-preview-canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      canvas = document.createElement('canvas');
      canvas.className = 'personalizador-preview-canvas';
      layer.appendChild(canvas);
    }

    state.layer = layer;
    state.canvas = canvas;
  }

  detachLayer(state) {
    if (state.targetImage) {
      state.targetImage.removeEventListener('load', state.onImageLoad);
    }

    state.targetImage = null;
    state.targetContainer = null;
    if (state.layer && state.layer.parentElement) {
      state.layer.parentElement.removeChild(state.layer);
    }

    state.layer = null;
    state.canvas = null;
  }

  render(state) {
    if (!(state.canvas instanceof HTMLCanvasElement) || !(state.targetContainer instanceof HTMLElement)) return;

    const containerRect = state.targetContainer.getBoundingClientRect();
    const width = Math.max(1, Math.round(containerRect.width));
    const height = Math.max(1, Math.round(containerRect.height));
    if (!width || !height) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);

    if (state.canvas.width !== pixelWidth || state.canvas.height !== pixelHeight) {
      state.canvas.width = pixelWidth;
      state.canvas.height = pixelHeight;
    }

    const ctx = state.canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const isCustomMode = this.getPersonalizadorMode(state.wrapper) === 'custom';
    const nameText = isCustomMode && state.nameInput ? (state.nameInput.value || '').toString().trim() : '';
    const flagState = isCustomMode ? this.resolveFlagState(state.wrapper) : { xPct: 50, yPct: 45, widthPx: 32, url: '' };
    const asaState = this.resolveAsaState(state.wrapper);

    if (!nameText && !flagState.url && !asaState.url) {
      this.detachLayer(state);
      return;
    }

    const cfg = this.readTextConfig(state);
    const imageBounds = this.resolveImageBounds(state, containerRect, width, height);
    state.lastImageBounds = imageBounds;

    // El tamaño base (ej. previewNameSizePx = 56) se asume configurado
    // visualmente para un ancho de imagen "desktop" estándar de 800px.
    // Esto asegura proporción fija en mobile/desktop sin depender de naturalWidth
    // que es inestable con imágenes responsive (srcset).
    const referenceWidth = 800;
    const displayScale = imageBounds.width / referenceWidth;

    let debugRect = null;

    ctx.save();
    ctx.beginPath();
    if (cfg.textAreaWidthPct > 0 && cfg.textAreaHeightPct > 0) {
      const areaW = (cfg.textAreaWidthPct / 100) * imageBounds.width;
      const areaH = (cfg.textAreaHeightPct / 100) * imageBounds.height;
      const centerX = imageBounds.x + (cfg.nameX / 100) * imageBounds.width;
      const centerY = imageBounds.y + (cfg.nameY / 100) * imageBounds.height;
      const renderFontSize = Math.max(1, cfg.nameSize * displayScale);
      const renderLineHeight = renderFontSize * 1.03;
      state.lastEnforceConfig = {
        areaW,
        maxLines: Math.max(1, Math.floor(areaH / renderLineHeight)),
        fontFamily: cfg.nameFontFamily,
        fontWeight: cfg.nameFontWeight,
        fontSize: renderFontSize,
      };
      ctx.rect(centerX - areaW / 2, centerY - areaH / 2, areaW, areaH);
      if (cfg.debugTextArea) {
        debugRect = { x: centerX - areaW / 2, y: centerY - areaH / 2, w: areaW, h: areaH };
      }
    } else {
      ctx.rect(imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height);
    }
    ctx.clip();

    if (flagState.url) {
      this.drawFlag(ctx, {
        areaX: imageBounds.x,
        areaY: imageBounds.y,
        areaWidth: imageBounds.width,
        areaHeight: imageBounds.height,
        scale: displayScale,
        xPct: flagState.xPct,
        yPct: flagState.yPct,
        widthPx: flagState.widthPx,
        url: flagState.url,
      });
    }

    if (nameText) {
      const displayText = cfg.wordPerLine
        ? nameText.split(/\s+/).filter(Boolean).join('\n')
        : nameText;
      this.drawMultilineText(ctx, {
        text: displayText,
        x: imageBounds.x + (cfg.nameX / 100) * imageBounds.width,
        y: imageBounds.y + (cfg.nameY / 100) * imageBounds.height,
        fontSize: Math.max(1, cfg.nameSize * displayScale),
        color: cfg.nameColor,
        colorImageUrl: cfg.nameColorImageUrl,
        rotationDeg: cfg.nameRotation,
        fontFamily: cfg.nameFontFamily,
        fontWeight: cfg.nameFontWeight,
        maxWidth: cfg.textAreaWidthPct > 0 && cfg.textAreaHeightPct > 0
          ? (cfg.textAreaWidthPct / 100) * imageBounds.width
          : 0,
        maxHeight: cfg.textAreaHeightPct > 0
          ? (cfg.textAreaHeightPct / 100) * imageBounds.height
          : 0,
      });
    }

    if (asaState.url) {
      this.drawAsa(ctx, {
        areaX: imageBounds.x,
        areaY: imageBounds.y,
        areaWidth: imageBounds.width,
        areaHeight: imageBounds.height,
        scale: displayScale,
        url: asaState.url,
      });
    }

    ctx.restore();

    if (debugRect) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.7)';
      ctx.lineWidth = 2;
      ctx.strokeRect(debugRect.x, debugRect.y, debugRect.w, debugRect.h);
      ctx.restore();
    }
  }

  resolveImageBounds(state, containerRect, containerWidth, containerHeight) {
    if (!(state.targetImage instanceof HTMLImageElement)) {
      return { x: 0, y: 0, width: containerWidth, height: containerHeight };
    }

    const imageRect = state.targetImage.getBoundingClientRect();
    const rawX = imageRect.left - containerRect.left;
    const rawY = imageRect.top - containerRect.top;
    const rawWidth = imageRect.width;
    const rawHeight = imageRect.height;

    if (rawWidth <= 0 || rawHeight <= 0) {
      return { x: 0, y: 0, width: containerWidth, height: containerHeight };
    }

    const x = this.clamp(rawX, 0, containerWidth);
    const y = this.clamp(rawY, 0, containerHeight);
    const width = Math.max(1, Math.min(rawWidth, containerWidth - x));
    const height = Math.max(1, Math.min(rawHeight, containerHeight - y));

    return { x, y, width, height };
  }

  resolveNaturalImageBounds(state, fallbackBounds) {
    if (!(state.targetImage instanceof HTMLImageElement)) {
      return {
        width: Math.max(1, fallbackBounds.width),
        height: Math.max(1, fallbackBounds.height),
      };
    }

    const naturalWidth = Number(state.targetImage.naturalWidth) || 0;
    const naturalHeight = Number(state.targetImage.naturalHeight) || 0;

    if (naturalWidth <= 0 || naturalHeight <= 0) {
      return {
        width: Math.max(1, fallbackBounds.width),
        height: Math.max(1, fallbackBounds.height),
      };
    }

    return {
      width: naturalWidth,
      height: naturalHeight,
    };
  }

  resolveFlagState(wrapper) {
    const flagBlock = wrapper.querySelector('.personalizador__block--bandera[data-preview-flag-x-pct]');
    if (!(flagBlock instanceof HTMLElement)) {
      return { xPct: 50, yPct: 45, widthPx: 32, url: '' };
    }

    return {
      xPct: this.clamp(this.toNumber(flagBlock.dataset.previewFlagXPct, 50), 0, 100),
      yPct: this.clamp(this.toNumber(flagBlock.dataset.previewFlagYPct, 45), 0, 100),
      widthPx: Math.max(1, this.toNumber(flagBlock.dataset.previewFlagWidthPx, 32)),
      url: (flagBlock.dataset.previewFlagUrl || '').toString().trim(),
    };
  }

  resolveAsaState(wrapper) {
    const asaBlock = wrapper.querySelector('.personalizador__block--asas[data-preview-asa-image-url]');
    if (!(asaBlock instanceof HTMLElement)) {
      return { url: '' };
    }

    return {
      url: (asaBlock.dataset.previewAsaImageUrl || '').toString().trim(),
    };
  }

  drawFlag(ctx, { areaX, areaY, areaWidth, areaHeight, scale, xPct, yPct, widthPx, url }) {
    const image = this.getFlagImage(url);
    if (!(image instanceof HTMLImageElement)) return;

    const drawWidth = Math.max(1, widthPx * scale);
    const defaultAspectRatio = 3 / 4;
    const aspectRatio = image.naturalWidth > 0 ? image.naturalHeight / image.naturalWidth : defaultAspectRatio;
    const drawHeight = Math.max(1, drawWidth * aspectRatio);

    const centerX = areaX + (xPct / 100) * areaWidth;
    const centerY = areaY + (yPct / 100) * areaHeight;

    ctx.save();
    ctx.drawImage(image, centerX - drawWidth / 2, centerY - drawHeight / 2, drawWidth, drawHeight);
    ctx.restore();
  }

  getFlagImage(url) {
    if (!url) return null;

    const cached = this.flagImageCache.get(url);
    if (cached) {
      if (cached.error) return null;
      return cached.loaded ? cached.image : null;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';

    const record = {
      image,
      loaded: false,
      error: false,
    };

    image.onload = () => {
      record.loaded = true;
      this.renderAll();
    };

    image.onerror = () => {
      record.error = true;
    };

    image.src = url;
    this.flagImageCache.set(url, record);

    return null;
  }

  drawAsa(ctx, { areaX, areaY, areaWidth, areaHeight, scale, url }) {
    const image = this.getAsaImage(url);
    if (!(image instanceof HTMLImageElement)) return;

    const maxWidth = areaWidth * 0.28;
    const maxHeight = areaHeight * 0.28;
    const ratio = image.naturalWidth > 0 && image.naturalHeight > 0
      ? Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight)
      : 1;
    const drawWidth = Math.max(1, image.naturalWidth * ratio);
    const drawHeight = Math.max(1, image.naturalHeight * ratio);
    const paddingX = areaWidth * 0.04;
    const paddingY = areaHeight * 0.04;
    const x = areaX + areaWidth - paddingX - drawWidth;
    const y = areaY + paddingY;

    ctx.save();
    ctx.drawImage(image, x, y, drawWidth, drawHeight);
    ctx.restore();
  }

  getAsaImage(url) {
    if (!url) return null;

    const cached = this.asaImageCache.get(url);
    if (cached) {
      if (cached.error) return null;
      return cached.loaded ? cached.image : null;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';

    const record = {
      image,
      loaded: false,
      error: false,
    };

    image.onload = () => {
      record.loaded = true;
      this.renderAll();
    };

    image.onerror = () => {
      record.error = true;
    };

    image.src = url;
    this.asaImageCache.set(url, record);

    return null;
  }

  drawMultilineText(ctx, { text, x, y, fontSize, color, colorImageUrl, rotationDeg = 0, fontFamily = '"Times New Roman", Times, serif', fontWeight = '600', maxWidth = 0, maxHeight = 0 }) {
    const rawLines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (rawLines.length === 0) return;

    const rotationRad = (Number(rotationDeg) * Math.PI) / 180;

    const colorImage = colorImageUrl ? this.getColorImage(colorImageUrl) : null;
    const pattern = colorImage ? ctx.createPattern(colorImage, 'repeat') : null;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Number.isFinite(rotationRad) ? rotationRad : 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = pattern || color;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

    if (document.fonts && !document.fonts.check(ctx.font)) {
      document.fonts.load(ctx.font).then(() => this.renderAll());
    }

    const allLines = maxWidth > 0
      ? rawLines.flatMap((l) => this.wrapTextLine(ctx, l, maxWidth))
      : rawLines;

    const lineHeight = fontSize * 1.03;
    const maxLines = maxHeight > 0 ? Math.max(1, Math.floor(maxHeight / lineHeight)) : Infinity;
    const lines = Number.isFinite(maxLines) ? allLines.slice(0, maxLines) : allLines;

    const firstLineY = -((lines.length - 1) * lineHeight) / 2;

    lines.forEach((line, index) => {
      ctx.fillText(line, 0, firstLineY + lineHeight * index);
    });

    ctx.restore();
  }

  wrapTextLine(ctx, text, maxWidth) {
    if (!text || ctx.measureText(text).width <= maxWidth) return [text];

    const words = text.split(' ');
    if (words.length > 1) {
      const lines = [];
      let current = '';
      for (const word of words) {
        const test = current ? current + ' ' + word : word;
        if (ctx.measureText(test).width > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) lines.push(current);
      return lines.flatMap((line) => this.wrapTextLine(ctx, line, maxWidth));
    }

    // Single word — character-level wrap
    const chars = Array.from(text);
    const lines = [];
    let current = '';
    for (const char of chars) {
      const test = current + char;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = char;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  enforceTextLimit(state) {
    const inputEl = state.nameInput;
    if (!(inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement)) return;

    const cfg = this.readTextConfig(state);
    if (!cfg.textAreaWidthPct || !cfg.textAreaHeightPct) return;

    // state.targetContainer puede ser null: render() llama detachLayer cuando no hay contenido
    // (modo 'none', input vacío). Resolvemos el container directamente para poder medir.
    const targetContainer = state.targetContainer instanceof HTMLElement
      ? state.targetContainer
      : this._findTargetContainerForMeasure(state);
    if (!(targetContainer instanceof HTMLElement)) return;

    const containerRect = targetContainer.getBoundingClientRect();
    if (!containerRect.width || !containerRect.height) return;
    const containerWidth = Math.round(containerRect.width);
    const containerHeight = Math.round(containerRect.height);

    const targetImage = state.targetImage instanceof HTMLImageElement
      ? state.targetImage
      : targetContainer.querySelector('img.product-media__image');
    let imageBounds;
    if (targetImage instanceof HTMLImageElement) {
      const imageRect = targetImage.getBoundingClientRect();
      if (imageRect.width > 0 && imageRect.height > 0) {
        const rawX = imageRect.left - containerRect.left;
        const rawY = imageRect.top - containerRect.top;
        const x = this.clamp(rawX, 0, containerWidth);
        const y = this.clamp(rawY, 0, containerHeight);
        imageBounds = {
          x, y,
          width: Math.max(1, Math.min(imageRect.width, containerWidth - x)),
          height: Math.max(1, Math.min(imageRect.height, containerHeight - y)),
        };
      }
    }
    if (!imageBounds) imageBounds = { x: 0, y: 0, width: containerWidth, height: containerHeight };
    if (!imageBounds.width || !imageBounds.height) return;

    const displayScale = imageBounds.width / 800;
    const fontSize = Math.max(1, cfg.nameSize * displayScale);
    const areaW = (cfg.textAreaWidthPct / 100) * imageBounds.width;
    const areaH = (cfg.textAreaHeightPct / 100) * imageBounds.height;
    const lineHeight = fontSize * 1.03;
    const maxLines = Math.max(1, Math.floor(areaH / lineHeight));

    const measureCanvas = state.canvas instanceof HTMLCanvasElement
      ? state.canvas
      : (this._measureCanvas = this._measureCanvas || document.createElement('canvas'));
    const ctx = measureCanvas.getContext('2d');
    if (!ctx) return;
    ctx.font = `${cfg.nameFontWeight} ${fontSize}px ${cfg.nameFontFamily}`;

    const fits = (value) => {
      if (cfg.wordPerLine) {
        // Cada palabra va en su propia línea, pero palabras largas también se char-wrapean
        const words = value.split(/\s+/).filter(Boolean);
        let totalLines = 0;
        for (const word of words) {
          totalLines += this.wrapTextLine(ctx, word, areaW).length;
          if (totalLines > maxLines) return false;
        }
        return true;
      }
      const rawLines = value.split(/\r?\n/).map((l) => l.trim());
      const wrapped = rawLines.flatMap((l) => l ? this.wrapTextLine(ctx, l, areaW) : ['']);
      return wrapped.filter(Boolean).length <= maxLines;
    };

    if (fits(inputEl.value)) return;

    let trimmed = inputEl.value;
    while (trimmed.length > 0 && !fits(trimmed)) {
      trimmed = trimmed.slice(0, -1);
    }
    inputEl.value = trimmed;
  }

  _findTargetContainerForMeasure(state) {
    const previewAnchor = state.nameBlock instanceof HTMLElement ? state.nameBlock : state.wrapper;
    const gallery = this.resolveGallery(previewAnchor);
    if (!(gallery instanceof HTMLElement)) return null;

    const configuredIndex = this.getTargetIndex(state);
    const gridVisible = this.isElementVisible(gallery.querySelector('.media-gallery__grid'));
    const slideshowContainers = Array.from(
      gallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide.product-media-container')
    );
    const gridContainers = Array.from(
      gallery.querySelectorAll('.media-gallery__grid > .product-media-container')
    );

    const slideshowTarget = this.pickTargetContainer(slideshowContainers, configuredIndex);
    const gridTarget = this.pickTargetContainer(gridContainers, configuredIndex);

    return gridVisible ? gridTarget || slideshowTarget : slideshowTarget || gridTarget;
  }

  getColorImage(url) {
    if (!url) return null;

    const cached = this.colorImageCache.get(url);
    if (cached) {
      if (cached.error) return null;
      return cached.loaded ? cached.image : null;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';

    const record = { image, loaded: false, error: false };

    image.onload = () => {
      record.loaded = true;
      this.renderAll();
    };

    image.onerror = () => {
      record.error = true;
    };

    image.src = url;
    this.colorImageCache.set(url, record);

    return null;
  }

  readTextConfig(state) {
    const block = state.nameBlock instanceof HTMLElement ? state.nameBlock : state.wrapper;
    const tipografiaBlock = state.wrapper instanceof HTMLElement
      ? state.wrapper.querySelector('.personalizador__block--tipografia')
      : null;
    return {
      nameX: this.clamp(this.toNumber(block.dataset.previewNameXPct, 50), 0, 100),
      nameY: this.clamp(this.toNumber(block.dataset.previewNameYPct, 52), 0, 100),
      nameSize: Math.max(1, this.toNumber(block.dataset.previewNameSizePx, 42)),
      nameColor: this.readColor(block.dataset.previewNameColor, '#FFFFFF'),
      nameColorImageUrl: (block.dataset.previewNameColorImageUrl || '').trim(),
      nameRotation: this.toNumber(block.dataset.previewNameRotationDeg, 0),
      nameFontFamily: (tipografiaBlock instanceof HTMLElement && tipografiaBlock.dataset.previewNameFontFamily)
        ? tipografiaBlock.dataset.previewNameFontFamily
        : '"Times New Roman", Times, serif',
      nameFontWeight: (tipografiaBlock instanceof HTMLElement && tipografiaBlock.dataset.previewNameFontWeight)
        ? tipografiaBlock.dataset.previewNameFontWeight
        : '600',
      textAreaWidthPct: this.toNumber(block.dataset.previewTextAreaWidthPct, 0),
      textAreaHeightPct: this.toNumber(block.dataset.previewTextAreaHeightPct, 0),
      debugTextArea: block.dataset.previewDebugTextArea === 'true',
      wordPerLine: block.dataset.previewWordPerLine === 'true',
    };
  }

  getTargetIndex(state) {
    if (state.nameBlock instanceof HTMLElement) {
      const raw = this.toNumber(state.nameBlock.dataset.previewTargetIndex, 1);
      return Math.max(0, Math.floor(raw) - 1);
    }
    if (state.asaBlock instanceof HTMLElement) {
      const raw = this.toNumber(state.asaBlock.dataset.previewAsaTargetIndex, 1);
      return Math.max(0, Math.floor(raw) - 1);
    }
    return 0;
  }

  toNumber(value, fallback) {
    const parsed = Number.parseFloat((value || '').toString());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  readColor(value, fallback) {
    const normalized = (value || '').toString().trim();
    if (!normalized) return fallback;
    if (window.CSS && typeof window.CSS.supports === 'function' && !window.CSS.supports('color', normalized)) {
      return fallback;
    }
    return normalized;
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  getPersonalizadorMode(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return 'none';
    return wrapper.dataset.personalizadorMode === 'custom' ? 'custom' : 'none';
  }

  shouldSwap(state) {
    if (!state.isPrimary) return false;
    if (this.getTargetIndex(state) === 0) return false;
    if (this.getPersonalizadorMode(state.wrapper) === 'custom') return true;
    return Boolean(this.resolveAsaState(state.wrapper).url);
  }

  updateSwapState(state) {
    if (this.shouldSwap(state)) {
      this.swapGridItemToFirst(state);
    } else {
      this.restoreGridOrder(state);
    }
  }

  handleModeChange(state) {
    this.updateSwapState(state);
    this.refreshTarget(state, { allowAutoScroll: true });
    this.render(state);
  }

  swapGridItemToFirst(state) {
    if (state.swapInfo) return;

    const configuredIndex = this.getTargetIndex(state);
    if (configuredIndex === 0) return;

    const grid = state.gallery instanceof HTMLElement
      ? state.gallery.querySelector('.media-gallery__grid')
      : null;
    if (!(grid instanceof HTMLElement)) return;

    const items = Array.from(grid.querySelectorAll(':scope > .product-media-container'));
    if (configuredIndex >= items.length) return;

    const targetItem = items[configuredIndex];
    const firstItem = items[0];
    if (!(targetItem instanceof HTMLElement) || !(firstItem instanceof HTMLElement)) return;

    state.swapInfo = { grid, originalIndex: configuredIndex };
    grid.insertBefore(targetItem, firstItem);
  }

  restoreGridOrder(state) {
    if (!state.swapInfo) return;

    const { grid, originalIndex } = state.swapInfo;
    state.swapInfo = null;

    if (!(grid instanceof HTMLElement)) return;

    const items = Array.from(grid.querySelectorAll(':scope > .product-media-container'));
    const swappedItem = items[0];
    if (!(swappedItem instanceof HTMLElement)) return;

    const refNode = items[originalIndex + 1] || null;
    grid.insertBefore(swappedItem, refNode);
  }

  autoScrollToTarget(state, targetIndex) {
    if (!state.isPrimary) return;
    if (state.hasAutoScrolled || !(state.targetContainer instanceof HTMLElement)) return;

    const currentMode = this.getPersonalizadorMode(state.wrapper);
    if (currentMode !== 'custom') return;

    state.hasAutoScrolled = true;

    const slideshow = this.resolveSlideshow(state.gallery);

    if (slideshow && typeof slideshow.select === 'function') {
      try {
        slideshow.select(targetIndex, undefined, { animate: false });
      } catch (error) {
        // Fallback to scroll only.
      }
    }

    window.setTimeout(() => {
      if (!(state.targetContainer instanceof HTMLElement)) return;
      state.targetContainer.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      this.render(state);
    }, 60);
  }
}

document.addEventListener('DOMContentLoaded', function () {
  window.personalizadorPreviewCanvas = new PersonalizadorPreviewCanvas();
});
